import {ChangeDetectionStrategy, Component, inject, OnInit, signal} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {ProgressSpinner} from 'primeng/progressspinner';
import {EMPTY, switchMap, take, tap} from 'rxjs';
import {MessageService} from 'primeng/api';
import {UserService} from '../../services/user.service';
import {PaymentService} from '../../services/payment/payment.service';

@Component({
  selector: 'homey-pay-redirect-page',
  standalone: true,
  imports: [ProgressSpinner, RouterLink],
  templateUrl: './pay-redirect-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PayRedirectPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly userService = inject(UserService);
  private readonly paymentService = inject(PaymentService);

  protected readonly error = signal<string | null>(null);
  protected readonly fallbackUrl = signal<string | null>(null);

  ngOnInit(): void {
    this.userService.user$.pipe(
      take(1),
      switchMap(user => {
        const id = this.route.snapshot.queryParamMap.get('id');
        const isLoggedIn = user.status !== 'unauthorized';

        if (!id && !isLoggedIn) {
          this.error.set('Invalid link. Please check your email or SMS for the correct link.');
          return EMPTY;
        }

        return this.paymentService.openPayRedirect(isLoggedIn ? null : id);
      }),
      tap(url => {
        if (url) {
          setTimeout(() => this.fallbackUrl.set(url), 2000);
        }
      })
    ).subscribe({
      error: (e: HttpErrorResponse) => {
        if (e.status === 404) {
          this.messageService.add({
            severity: 'info',
            summary: 'No outstanding payment',
            detail: 'There is no open invoice to pay. Your payment may have already been processed.',
          });
          this.userService.retrieveUser().subscribe(() => {
            void this.router.navigate(['/']);
          });
        } else {
          this.error.set('Something went wrong. Please try again or contact support.');
        }
      }
    });
  }
}
